import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Box,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import { NavigateNext, ArrowBack, Edit, Print, Download } from '@mui/icons-material';
import AcademicQuestionPaperDetails from './AcademicQuestionPaperDetails';
import { academicQuestionPaperApi } from './academicQuestionPaperApi';

const ViewQuestionPaper = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionPaper, setQuestionPaper] = useState(null);

  useEffect(() => {
    fetchQuestionPaper();
  }, [id]);

  const fetchQuestionPaper = async () => {
    try {
      const response = await academicQuestionPaperApi.getById(id);
      if (response.success && response.data) {
        setQuestionPaper(response.data);
      } else {
        setError('Failed to load question paper data');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load question paper';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Implement download functionality (e.g., generate PDF)
    alert('Download functionality will be implemented here');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/question-papers')}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link color="inherit" href="/">
          Home
        </Link>
        <Link color="inherit" href="/question-papers">
          Question Papers
        </Link>
        <Typography color="text.primary">View</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Question Paper Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View the complete details of the question paper.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/question-papers')}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/question-papers/edit/${id}`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownload}
          >
            Download
          </Button>
        </Stack>
      </Box>

      <AcademicQuestionPaperDetails data={questionPaper} />
    </Container>
  );
};

export default ViewQuestionPaper;